import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";

/**
 * Everything except the landing page is operator-only.
 *
 * This is a courtesy, not the enforcement: the API gates the same routes on the
 * server, so a visitor who types the URL gets this screen and a 403 from every
 * request behind it.
 *
 * Sessions come from ch-watcher — this app mints none. Since localStorage is
 * per-origin, being signed in there does nothing here, so the session has to be
 * carried over: either by a link carrying ?token=, or pasted below.
 */
export default function AdminRoute({ children }: { children: ReactNode }) {
  const { isAdmin, authenticated, loading, adoptToken } = useAuth();
  const [token, setToken] = useState("");
  const [showPaste, setShowPaste] = useState(false);

  if (loading) {
    return (
      <div className="ap-page">
        <div className="ap-state">Checking access…</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="ap-page ap-gate">
        <p className="ap-gate__msg">This page is for operators</p>

        <Link to="/" className="ap-btn">
          Return Home
        </Link>

        {/* A signed-in non-admin is a different problem from no session at all,
            and pasting another token will not solve it. */}
        {authenticated ? (
          <p className="ap-muted">This account is signed in, but is not an operator.</p>
        ) : showPaste ? (
          <form
            className="ap-gate__form"
            onSubmit={(e) => {
              e.preventDefault();
              adoptToken(token);
              setToken("");
            }}
          >
            <label className="ap-muted" htmlFor="gate-token">
              Paste a session token from the main watcher
            </label>
            <input
              id="gate-token"
              className="ap-input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="eyJhbGciOi…"
            />
            <button type="submit" className="ap-btn ap-btn--sm" disabled={!token.trim()}>
              Use this session
            </button>
          </form>
        ) : (
          <button
            type="button"
            className="ap-btn ap-btn--sm ap-btn--ghost"
            onClick={() => setShowPaste(true)}
          >
            Sign in with a session token
          </button>
        )}
      </div>
    );
  }

  return <>{children}</>;
}
